<template>
  <b-modal
    :visible="value"
    :title="isNew ? 'Новая механика' : `Механика «${mechanic.name}»`"
    size="xl"
    no-close-on-backdrop
    @change="onVisibleChange"
    @show="resetForm"
  >
    <b-row>
      <b-col md="5">
        <b-form-group label="Название" :state="fieldState('name')" :invalid-feedback="fieldError('name')">
          <b-form-input v-model="form.name" :state="fieldState('name')" />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Код" :state="fieldState('code')" :invalid-feedback="fieldError('code')">
          <b-form-input v-model="form.code" :state="fieldState('code')" placeholder="необязательно" />
        </b-form-group>
      </b-col>
      <b-col md="2">
        <b-form-group label="Контент" :state="fieldState('content_kind')" :invalid-feedback="fieldError('content_kind')">
          <b-form-select v-model="form.content_kind" :options="contentKindOptions" :state="fieldState('content_kind')" />
        </b-form-group>
      </b-col>
      <b-col md="2">
        <b-form-group label="Порядок" :state="fieldState('order')" :invalid-feedback="fieldError('order')">
          <b-form-input v-model="form.order" type="number" :state="fieldState('order')" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col md="4">
        <b-form-group label="Группа" :state="fieldState('group')" :invalid-feedback="fieldError('group')">
          <b-form-input
            v-model="form.group"
            list="mechanic-group-suggestions"
            maxlength="64"
            placeholder="необязательно, можно выбрать существующую"
            :state="fieldState('group')"
          />
          <datalist id="mechanic-group-suggestions">
            <option v-for="groupName in groupSuggestions" :key="groupName" :value="groupName" />
          </datalist>
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group label="Описание" :state="fieldState('description')" :invalid-feedback="fieldError('description')">
      <b-form-textarea v-model="form.description" rows="2" max-rows="6" :state="fieldState('description')" />
    </b-form-group>

    <b-form-group :state="fieldState('is_archived')" :invalid-feedback="fieldError('is_archived')">
      <b-form-checkbox v-model="form.is_archived">В архиве</b-form-checkbox>
    </b-form-group>

    <!-- Конструктор слотов: формат как MechanicSlots::validate -->
    <div class="d-flex align-items-center mb-50">
      <h5 class="mb-0 mr-1">Слоты</h5>
      <small class="text-muted">
        без слотов механика ведёт себя как один слот «Ответ» (исполнитель для музыки, фильм для кино)
      </small>
    </div>

    <div v-if="form.slots.length" class="d-flex small text-muted mb-25 slot-header">
      <span class="slot-handle-col" />
      <span class="slot-code-col">Код</span>
      <span class="slot-name-col">Название</span>
      <span class="slot-entity-col">Сущность</span>
      <span class="flex-grow-1">Флаги</span>
    </div>

    <draggable
      :list="form.slots"
      handle=".slot-handle"
      tag="div"
    >
      <div
        v-for="(slot, index) in form.slots"
        :key="slot.uid"
        class="d-flex align-items-center flex-wrap border rounded p-50 mb-50"
      >
        <span class="slot-handle-col">
          <feather-icon icon="MenuIcon" class="slot-handle cursor-move text-muted" />
        </span>
        <span class="slot-code-col pr-50">
          <b-form-input
            v-model="slot.code"
            size="sm"
            placeholder="code"
            :state="isSlotCodeValid(slot.code) ? null : false"
            title="Латиница в нижнем регистре, цифры и «_», с буквы, до 32 символов"
          />
        </span>
        <span class="slot-name-col pr-50">
          <b-form-input v-model="slot.name" size="sm" placeholder="Название" />
        </span>
        <span class="slot-entity-col pr-50">
          <b-form-select v-model="slot.entity" :options="entityOptions" size="sm" />
        </span>
        <span class="flex-grow-1 d-flex align-items-center flex-wrap">
          <b-form-checkbox v-model="slot.multiple" class="mr-1">несколько</b-form-checkbox>
          <b-form-checkbox v-model="slot.required" class="mr-1">обязательный</b-form-checkbox>
          <b-form-checkbox v-model="slot.counts_as_played" class="mr-1">учитывать как отыгрыш</b-form-checkbox>
        </span>
        <b-button
          variant="flat-danger"
          size="sm"
          class="btn-icon"
          title="Удалить слот"
          @click="removeSlot(index)"
        >
          <feather-icon icon="Trash2Icon" />
        </b-button>
      </div>
    </draggable>

    <div v-if="fieldError('slots')" class="text-danger small mb-50">{{ fieldError('slots') }}</div>

    <b-button variant="outline-primary" size="sm" @click="addSlot">
      <feather-icon icon="PlusIcon" class="mr-25" />
      Добавить слот
    </b-button>

    <template #modal-footer>
      <b-button variant="flat-secondary" @click="close">Отмена</b-button>
      <b-button variant="primary" :disabled="isSaving" @click="save">
        <b-spinner v-if="isSaving" small class="mr-50" />
        Сохранить
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BButton, BCol, BFormCheckbox, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BModal, BRow, BSpinner,
} from 'bootstrap-vue'
import draggable from 'vuedraggable'
import { computed, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

const MODULE = 'question-storage'
// Как MechanicSlots::CODE_PATTERN
const SLOT_CODE_PATTERN = /^[a-z][a-z0-9_]{0,31}$/

let slotUid = 0
const nextSlotUid = () => {
  slotUid += 1

  return slotUid
}

const toSlotRow = slot => ({
  uid: nextSlotUid(),
  code: slot.code || '',
  name: slot.name || '',
  entity: slot.entity || 'artist',
  multiple: Boolean(slot.multiple),
  required: Boolean(slot.required),
  counts_as_played: slot.counts_as_played === undefined ? true : Boolean(slot.counts_as_played),
})

export default {
  components: {
    BButton, BCol, BFormCheckbox, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BModal, BRow, BSpinner, draggable,
  },
  props: {
    // Видимость модалки (v-model)
    value: { type: Boolean, default: false },
    // Редактируемая механика; null — новая
    mechanic: { type: Object, default: null },
    // Существующие группы для подсказок
    groupSuggestions: { type: Array, default: () => [] },
  },
  setup(props, { emit }) {
    const toast = useToast()

    const contentKindOptions = [
      { value: 'music', text: 'Музыка' },
      { value: 'movies', text: 'Кино' },
      { value: 'any', text: 'Любой' },
    ]
    // Сущности MechanicSlots::ENTITIES
    const entityOptions = [
      { value: 'artist', text: 'Исполнитель' },
      { value: 'track', text: 'Трек' },
      { value: 'movie', text: 'Фильм/сериал' },
      { value: 'artist_cover', text: 'Обложка' },
      { value: 'text', text: 'Текст' },
    ]

    const isNew = computed(() => !props.mechanic || !props.mechanic.id)
    const form = ref({
      name: '', code: '', content_kind: 'music', group: '', description: '', is_archived: false, order: '', slots: [],
    })
    const errors = ref({})
    const isSaving = ref(false)

    const resetForm = () => {
      const mechanic = props.mechanic || {}
      form.value = {
        name: mechanic.name || '',
        code: mechanic.code || '',
        content_kind: mechanic.content_kind || 'music',
        group: mechanic.group || '',
        description: mechanic.description || '',
        is_archived: Boolean(mechanic.is_archived),
        order: mechanic.order === null || mechanic.order === undefined ? '' : String(mechanic.order),
        slots: (Array.isArray(mechanic.slots) ? mechanic.slots : []).map(toSlotRow),
      }
      errors.value = {}
    }

    const fieldError = field => (errors.value[field] && errors.value[field][0]) || ''
    const fieldState = field => (fieldError(field) ? false : null)
    const isSlotCodeValid = code => SLOT_CODE_PATTERN.test(code || '')

    const addSlot = () => {
      form.value.slots.push(toSlotRow({}))
    }
    const removeSlot = index => {
      form.value.slots.splice(index, 1)
    }

    const close = () => emit('input', false)
    const onVisibleChange = visible => emit('input', visible)

    const buildPayload = () => {
      const order = String(form.value.order).trim()

      return {
        name: form.value.name.trim(),
        code: form.value.code.trim() === '' ? null : form.value.code.trim(),
        content_kind: form.value.content_kind,
        group: form.value.group.trim() === '' ? null : form.value.group.trim(),
        description: form.value.description.trim() === '' ? null : form.value.description,
        is_archived: form.value.is_archived,
        order: order === '' ? null : parseInt(order, 10),
        slots: form.value.slots.map(slot => ({
          code: slot.code.trim(),
          name: slot.name.trim() === '' ? slot.code.trim() : slot.name.trim(),
          entity: slot.entity,
          multiple: slot.multiple,
          required: slot.required,
          counts_as_played: slot.counts_as_played,
        })),
      }
    }

    const save = () => {
      errors.value = {}
      isSaving.value = true
      const payload = buildPayload()
      const request = isNew.value
        ? store.dispatch(`${MODULE}/createMechanic`, payload)
        : store.dispatch(`${MODULE}/updateMechanic`, { id: props.mechanic.id, data: payload })

      request
        .then(response => {
          toast({
            component: ToastificationContent,
            props: { title: isNew.value ? 'Механика создана' : 'Механика сохранена', icon: 'CheckIcon', variant: 'success' },
          })
          emit('saved', response.data.data)
          close()
        })
        .catch(error => {
          const data = error && error.response && error.response.data
          if (error && error.response && error.response.status === 422 && data && data.errors) {
            errors.value = data.errors
            return
          }
          toast({
            component: ToastificationContent,
            props: { title: (data && data.message) || 'Не удалось сохранить механику', icon: 'AlertTriangleIcon', variant: 'danger' },
          })
        })
        .finally(() => { isSaving.value = false })
    }

    return {
      contentKindOptions,
      entityOptions,
      isNew,
      form,
      isSaving,
      resetForm,
      fieldError,
      fieldState,
      isSlotCodeValid,
      addSlot,
      removeSlot,
      close,
      onVisibleChange,
      save,
    }
  },
}
</script>

<style scoped>
.slot-handle-col {
  width: 28px;
  flex-shrink: 0;
}
.slot-code-col {
  width: 160px;
}
.slot-name-col {
  width: 220px;
}
.slot-entity-col {
  width: 160px;
}
</style>
